/**
 * Emoji: the picker (docs/design/visual-overhaul.md §7.9, §7.0 rule 3:
 * 8 x 40px cells, nine category tabs, sticky headers, a preview footer),
 * the person's recent emoji (Frequently used and the hover toolbar's three
 * quick reactions), and how an emoji is drawn.
 *
 * Drawing (R8, T12): an emoji is <img class="nx-emoji"> from the bundled
 * Fluent Emoji Flat set in ../assets/emoji (named by code points, e.g.
 * 1f44d.svg; MIT, LICENSE-FluentEmoji.txt), served from our own origin and
 * never fetched from anywhere else. An emoji the set does not have (country
 * flags: Fluent has none) is the system's glyph in a <span class="nx-emoji">
 * of the same size, so layout never changes. Copying yields the Unicode
 * text either way (the image's alt). Textareas keep the OS glyphs.
 *
 * Custom server emoji (customEmoji.tsx, 2026-11-01) travel as their message
 * form `<:name:id>` in every string here; `Emoji` draws them, the picker
 * lists them first (the server's section), and recents and quick reactions
 * keep them while the server still has them.
 */
import { readLayout } from '@neosocial/ui/layout';
import { Sheet } from '@neosocial/ui/sheet';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { base as SET_BASE, keys as SET_KEYS } from 'virtual:emoji-set';
import { create } from 'zustand';
import { useT } from '../i18n';
import { storage } from '../lib/storage';
import { Icon } from '../ui/kit';
import { openPopover, type Anchor, type Placement } from '../ui/overlay';
import { useCustomEmojiOn, useEmoji } from '../store/emoji';
import { CustomEmoji, customName, isCustomToken, isDrawableCustom, tokenOf } from './customEmoji';

/* ------------------------------------------------------------ drawing */

// The keys only (emojiSet() in vite.config.ts emits the files); a file loads
// when an <img> asks for it: our origin, lazily
const KEYS = new Set(SET_KEYS ? SET_KEYS.split(' ') : []);

/** The set's file name for an emoji: its code points in hex, without VS16. */
export function emojiKey(e: string): string {
  return [...e]
    .map((c) => c.codePointAt(0)!.toString(16))
    .filter((h) => h !== 'fe0f')
    .join('-');
}

export function emojiUrl(e: string): string | null {
  const k = emojiKey(e);
  return KEYS.has(k) ? `${SET_BASE}${k}.svg` : null;
}

export function Emoji({ e, cls }: { e: string; cls?: string }) {
  if (isCustomToken(e)) return <CustomEmoji token={e} cls={cls} />;
  const url = emojiUrl(e);
  const c = 'nx-emoji' + (cls ? ' ' + cls : '');
  if (url)
    return (
      <img className={c} src={url} alt={e} draggable={false} decoding="async" loading="lazy" />
    );
  return <span className={c + ' is-text'}>{e}</span>;
}

/** One emoji: a flag pair, a keycap, or a pictograph with its modifiers and ZWJ parts. */
const EMOJI_SRC =
  '\\p{Regional_Indicator}{2}|[#*0-9]\\uFE0F\\u20E3|(?:\\p{Emoji_Presentation}|\\p{Extended_Pictographic}\\uFE0F)\\p{Emoji_Modifier}?(?:\\u200D(?:\\p{Emoji_Presentation}|\\p{Extended_Pictographic}\\uFE0F?)\\p{Emoji_Modifier}?)*';
const EMOJI_RE = new RegExp(EMOJI_SRC, 'gu');
const ONLY_RE = new RegExp(`^(?:\\s|${EMOJI_SRC})+$`, 'u');

/** Text with its emoji drawn as emoji (§7.9): strings and <Emoji> nodes. */
export function emojiNodes(text: string, key: string): ReactNode[] {
  const re = new RegExp(EMOJI_RE.source, 'gu');
  const out: ReactNode[] = [];
  let last = 0;
  let n = 0;
  for (let m = re.exec(text); m; m = re.exec(text)) {
    if (m.index > last) out.push(text.slice(last, m.index));
    out.push(<Emoji key={`${key}-e${n++}`} e={m[0]} />);
    last = m.index + m[0].length;
  }
  if (!out.length) return [text];
  if (last < text.length) out.push(text.slice(last));
  return out;
}

const CUSTOM_G = /<a?:[A-Za-z0-9_]{1,32}:\d{1,20}>/g;

/**
 * A message of only emoji (up to 27) is drawn jumbo (48px on a 56px line);
 * custom emoji count when the app can draw them (Discord).
 */
export function isJumbo(text: string): boolean {
  const s = text.trim();
  if (!s) return false;
  const custom = s.match(CUSTOM_G) ?? [];
  if (!custom.every(isDrawableCustom)) return false;
  const rest = custom.length ? s.replace(CUSTOM_G, ' ').trim() : s;
  if (rest && !ONLY_RE.test(rest)) return false;
  const n = custom.length + (rest.match(new RegExp(EMOJI_RE.source, 'gu'))?.length ?? 0);
  return n > 0 && n <= 27;
}

/* ------------------------------------------------------------ the set */

export interface EmojiItem {
  e: string;
  n: string;
}
export type EmojiCat =
  | 'frequent'
  | 'server'
  | 'people'
  | 'nature'
  | 'food'
  | 'activities'
  | 'travel'
  | 'objects'
  | 'symbols'
  | 'flags';

/** "emoji name|emoji name": the set by category (no country of the app's own; English names). */
const DATA: Record<Exclude<EmojiCat, 'frequent' | 'server'>, string> = {
  people:
    '😀 grinning|😃 smiley|😄 smile|😁 grin|😆 laughing|😅 sweat smile|🤣 rofl|😂 joy laugh|🙂 slight smile|🙃 upside down|🥲 smiling tear|🫠 melting|😉 wink|😊 blush|😇 innocent halo|🥰 smiling hearts|😍 heart eyes love|🤩 star struck|😘 kiss|😋 yum|😛 tongue|😜 winking tongue|🤪 zany|🤑 money mouth|🤗 hug|🤭 hand over mouth|🫡 salute|🤫 shush|🤔 thinking hmm|🤐 zipper mouth|🤨 raised eyebrow|😐 neutral|😑 expressionless|😶 no mouth|😏 smirk|😒 unamused|🙄 eye roll|😬 grimace|😌 relieved|😔 pensive|😪 sleepy|😴 sleeping|😷 mask|🤒 thermometer sick|🤢 nauseated|🤮 vomit|🥵 hot|🥶 cold|🥴 woozy|😵 dizzy|🤯 mind blown|🤠 cowboy|🥳 party|😎 cool sunglasses|🤓 nerd|🧐 monocle|😕 confused|😟 worried|🙁 frown|😮 open mouth|😲 astonished|😳 flushed|🥺 pleading|🥹 holding back tears|😨 fearful|😰 anxious|😢 cry|😭 sob|😱 scream|😖 confounded|😞 disappointed|😓 sweat|😩 weary|😫 tired|🥱 yawn|😤 triumph|😡 rage|😠 angry|🤬 cursing|😈 smiling imp|💀 skull dead|💩 poop|🤡 clown|👻 ghost|👽 alien|🤖 robot|👋 wave hello bye|🤚 raised back of hand|✋ raised hand|👌 ok hand|🤌 pinched fingers|✌️ victory peace|🤞 crossed fingers|🤟 love you|🤘 rock on|🤙 call me|👈 point left|👉 point right|👆 point up|👇 point down|👍 thumbs up yes like|👎 thumbs down no|✊ fist|👊 punch|👏 clap|🙌 raised hands|🫶 heart hands|👐 open hands|🤲 palms up|🤝 handshake deal|🙏 pray please thanks|💪 muscle strong|👀 eyes look|🧠 brain',
  nature:
    '🐶 dog|🐱 cat|🐭 mouse|🐹 hamster|🐰 rabbit|🦊 fox|🐻 bear|🐼 panda|🐨 koala|🐯 tiger|🦁 lion|🐮 cow|🐷 pig|🐸 frog|🐵 monkey|🐔 chicken|🐧 penguin|🐦 bird|🦅 eagle|🦉 owl|🐺 wolf|🐴 horse|🦄 unicorn|🐝 bee|🦋 butterfly|🐢 turtle|🐍 snake|🐙 octopus|🐬 dolphin|🐳 whale|🦈 shark|🌵 cactus|🌲 evergreen tree|🌳 tree|🌴 palm tree|🌱 seedling|🍀 four leaf clover|🍁 maple leaf|🌸 cherry blossom|🌹 rose|🌻 sunflower|🌷 tulip|🌙 moon night|⭐ star|🌟 glowing star|☀️ sun|⛅ sun behind cloud|🌧️ rain|⛈️ storm|❄️ snowflake|🌈 rainbow|🔥 fire hot lit|💧 droplet|🌊 water wave',
  food: '🍏 green apple|🍎 apple|🍐 pear|🍊 orange|🍋 lemon|🍌 banana|🍉 watermelon|🍇 grapes|🍓 strawberry|🍒 cherries|🍑 peach|🥭 mango|🍍 pineapple|🥥 coconut|🥝 kiwi|🍅 tomato|🥑 avocado|🌶️ hot pepper|🌽 corn|🥕 carrot|🥐 croissant|🍞 bread|🧀 cheese|🥚 egg|🍳 cooking|🥞 pancakes|🥓 bacon|🍔 burger|🍟 fries|🍕 pizza|🌭 hot dog|🥪 sandwich|🌮 taco|🌯 burrito|🍝 spaghetti|🍜 ramen noodles|🍣 sushi|🍚 rice|🍰 cake|🎂 birthday cake|🍩 doughnut|🍪 cookie|🍫 chocolate|🍿 popcorn|🍦 ice cream|☕ coffee|🍵 tea|🧋 bubble tea|🥤 cup with straw|🧃 juice',
  activities:
    '⚽ soccer football|🏀 basketball|🏈 american football|⚾ baseball|🎾 tennis|🏐 volleyball|🏉 rugby|🎱 pool 8 ball|🏓 ping pong|🏸 badminton|🥊 boxing|🥋 martial arts|⛳ golf|⛸️ ice skate|🎿 ski|🏂 snowboard|🚴 cycling|🏆 trophy win|🥇 gold medal first|🥈 silver medal|🥉 bronze medal|🏅 medal|🎮 game controller|🕹️ joystick|🎯 target bullseye|🎲 dice|♟️ chess|🧩 puzzle|🎨 art palette|🎭 theater|🎤 microphone sing|🎧 headphones music|🎸 guitar|🎹 piano|🥁 drum|🎬 clapper film|🎉 tada party celebrate|🎊 confetti|🎁 gift|🎈 balloon',
  travel:
    '🚗 car|🚕 taxi|🚌 bus|🏎️ race car|🚓 police car|🚑 ambulance|🚒 fire engine|🚚 truck|🚲 bicycle|🛵 scooter|🏍️ motorcycle|🚂 train|🚇 metro|✈️ airplane|🚀 rocket launch|🛸 ufo|🚁 helicopter|⛵ sailboat|🚢 ship|⚓ anchor|🗺️ map|🧭 compass|🏔️ snowy mountain|🏕️ camping|🏖️ beach|🏝️ island|🏠 house home|🏢 office|🏰 castle|🗼 tower|🌆 city dusk|🌃 night city|🎡 ferris wheel|🎢 roller coaster',
  objects:
    '⌚ watch|📱 phone|💻 laptop|⌨️ keyboard|🖥️ desktop computer|🖱️ computer mouse|💾 floppy disk save|📷 camera|🎥 movie camera|📺 television|📻 radio|🎙️ studio microphone mic|⏰ alarm clock|⌛ hourglass|🔋 battery|🔌 plug|💡 light bulb idea|🔦 flashlight|🕯️ candle|💸 money with wings|💰 money bag|💎 gem|🔧 wrench|🔨 hammer|🛠️ tools|⚙️ gear|🔒 lock|🔑 key|🛡️ shield|⚔️ crossed swords|💣 bomb|🧲 magnet|📌 pushpin|📎 paperclip|✏️ pencil|📝 memo|📚 books|📦 package|✉️ envelope|📣 megaphone|🔔 bell',
  symbols:
    '❤️ red heart love|🧡 orange heart|💛 yellow heart|💚 green heart|💙 blue heart|💜 purple heart|🖤 black heart|🤍 white heart|🤎 brown heart|💔 broken heart|❣️ heart exclamation|💕 two hearts|💞 revolving hearts|💖 sparkling heart|💯 hundred perfect|💢 anger|💥 boom|💫 dizzy star|💦 sweat drops|💤 zzz sleep|✨ sparkles|⚡ zap lightning|✅ check mark done yes|✔️ heavy check|❌ cross mark no|❎ cross button|❓ question|❗ exclamation|⁉️ interrobang|‼️ double exclamation|⚠️ warning|🚫 prohibited|⛔ no entry|♻️ recycle|🔴 red circle|🟠 orange circle|🟡 yellow circle|🟢 green circle|🔵 blue circle|🟣 purple circle|⚫ black circle|⚪ white circle|➕ plus|➖ minus|✖️ multiply|🆕 new|🆗 ok button|🆒 cool button|🎵 music note|🎶 notes',
  flags:
    '🏁 chequered flag|🚩 red flag|🎌 crossed flags|🏴 black flag|🏳️ white flag|🏳️‍🌈 rainbow flag|🏴‍☠️ pirate flag|🇺🇳 united nations|🇺🇸 united states|🇬🇧 united kingdom|🇨🇦 canada|🇦🇺 australia|🇩🇪 germany|🇫🇷 france|🇪🇸 spain|🇮🇹 italy|🇳🇱 netherlands|🇸🇪 sweden|🇳🇴 norway|🇫🇮 finland|🇩🇰 denmark|🇨🇭 switzerland|🇦🇹 austria|🇵🇱 poland|🇺🇦 ukraine|🇯🇵 japan|🇰🇷 south korea|🇨🇳 china|🇮🇳 india|🇧🇷 brazil|🇲🇽 mexico|🇦🇷 argentina|🇿🇦 south africa',
};

const parse = (s: string): EmojiItem[] =>
  s.split('|').map((x) => {
    const sp = x.indexOf(' ');
    return { e: x.slice(0, sp), n: x.slice(sp + 1) };
  });

export const EMOJI: Record<
  Exclude<EmojiCat, 'frequent' | 'server'>,
  EmojiItem[]
> = Object.fromEntries(Object.entries(DATA).map(([k, v]) => [k, parse(v)])) as Record<
  Exclude<EmojiCat, 'frequent' | 'server'>,
  EmojiItem[]
>;

const NAMES = new Map<string, string>(
  Object.values(EMOJI)
    .flat()
    .map((x) => [x.e, x.n]),
);
/** The emoji's name as the picker's footer shows it: "thumbs_up" (a custom one's own name). */
export function emojiName(e: string): string {
  if (isCustomToken(e)) return customName(e) ?? '';
  return (NAMES.get(e) ?? '').split(' ').slice(0, 2).join('_');
}

/** The tab glyph of each category (the set's own emoji, so they are drawn as emoji). */
const TAB: Record<Exclude<EmojiCat, 'server'>, string> = {
  frequent: '🕒',
  people: '😀',
  nature: '🐻',
  food: '🍔',
  activities: '⚽',
  travel: '✈️',
  objects: '💡',
  symbols: '💜',
  flags: '🏁',
};
export const CATS: EmojiCat[] = [
  'frequent',
  'people',
  'nature',
  'food',
  'activities',
  'travel',
  'objects',
  'symbols',
  'flags',
];

/* ------------------------------------------------------------ recents */

const FALLBACK_QUICK = ['👍', '😂', '🔥'];
const FALLBACK_FREQUENT = ['👍', '😂', '🔥', '💜', '🎉', '😭', '🙏', '👀', '💀', '🤯', '😎', '👋'];
const RECENT_KEY = 'emoji-recent';

let recents: string[] | null = null;
let quick: string[] | null = null;

/** The person's recent emoji, newest first (this device); a custom one only while it can be drawn. */
export function recentEmoji(): string[] {
  if (!recents) {
    const v = storage.getJson<unknown>(RECENT_KEY, []);
    recents = Array.isArray(v)
      ? v.filter((x): x is string => typeof x === 'string').slice(0, 24)
      : [];
  }
  return recents.filter((e) => !isCustomToken(e) || isDrawableCustom(e));
}
export function pushRecent(e: string): void {
  recents = [e, ...recentEmoji().filter((x) => x !== e)].slice(0, 24);
  quick = null;
  storage.setJson(RECENT_KEY, recents);
}
/** The hover toolbar's three quick reactions: the three most recent, then 👍😂🔥 (§7.10). */
export function quickReactions(): string[] {
  // a custom emoji deleted (or turned off) since leaves the three
  if (quick?.some((e) => isCustomToken(e) && !isDrawableCustom(e))) quick = null;
  if (!quick) {
    const out = recentEmoji().slice(0, 3);
    for (const e of FALLBACK_QUICK) if (out.length < 3 && !out.includes(e)) out.push(e);
    quick = out;
  }
  return quick;
}
function frequent(): EmojiItem[] {
  const out = recentEmoji();
  for (const e of FALLBACK_FREQUENT) if (out.length < 16 && !out.includes(e)) out.push(e);
  return out
    .slice(0, 16)
    .map((e) => ({ e, n: isCustomToken(e) ? (customName(e) ?? '') : (NAMES.get(e) ?? '') }));
}

/* ------------------------------------------------------------ the picker */

const LABEL: Record<EmojiCat, string> = {
  frequent: 'hub.emoji.frequent',
  server: 'emo.server',
  people: 'chat.emoji.people',
  nature: 'chat.emoji.nature',
  food: 'hub.emoji.food',
  activities: 'chat.emoji.activities',
  travel: 'chat.emoji.travel',
  objects: 'chat.emoji.objects',
  symbols: 'chat.emoji.symbols',
  flags: 'chat.emoji.flags',
};

export function EmojiPicker({ onPick, close }: { onPick(e: string): void; close(): void }) {
  const { t } = useT();
  const [q, setQ] = useState('');
  const [active, setActive] = useState<EmojiCat>('frequent');
  const [hover, setHover] = useState<EmojiItem | null>(null);
  const body = useRef<HTMLDivElement>(null);
  // the server's own emoji, first after Frequently used (Discord), while the console allows them
  const customOn = useCustomEmojiOn();
  const byGuild = useEmoji((s) => s.byGuild);
  const server = useMemo(
    (): EmojiItem[] =>
      customOn
        ? Object.values(byGuild)
            .flat()
            .map((x) => ({ e: tokenOf(x), n: x.name }))
        : [],
    [customOn, byGuild],
  );
  const cats = useMemo(
    (): EmojiCat[] => (server.length ? ['frequent', 'server', ...CATS.slice(1)] : CATS),
    [server.length],
  );
  const groups = useMemo((): Array<[EmojiCat | 'results', EmojiItem[]]> => {
    const query = q.trim().toLowerCase();
    if (!query)
      return [
        ['frequent', frequent()],
        ...(server.length ? [['server', server] as [EmojiCat, EmojiItem[]]] : []),
        ...CATS.slice(1).map(
          (c) =>
            [c, EMOJI[c as Exclude<EmojiCat, 'frequent' | 'server'>]] as [EmojiCat, EmojiItem[]],
        ),
      ];
    const all = [...server, ...Object.values(EMOJI).flat()];
    const key = query.replace(/^:|:$/g, '');
    return [['results', all.filter((x) => x.n.includes(key) || x.e === query)]];
  }, [q, server]);
  const empty = groups.every(([, l]) => !l.length);

  // the tab follows the scroll: the last section whose header has passed the top
  useEffect(() => {
    const el = body.current;
    if (!el || q) return;
    const onScroll = () => {
      const top = el.getBoundingClientRect().top;
      let cur: EmojiCat = 'frequent';
      for (const s of el.querySelectorAll<HTMLElement>('[data-cat]'))
        if (s.getBoundingClientRect().top - top <= 8) cur = s.dataset.cat as EmojiCat;
      setActive(cur);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [q]);

  const pick = (it: EmojiItem, shift: boolean) => {
    pushRecent(it.e);
    onPick(it.e);
    if (!shift) close();
  };
  const go = (c: EmojiCat) => {
    setQ('');
    setActive(c);
    requestAnimationFrame(() => {
      const el = body.current;
      const s = el?.querySelector<HTMLElement>(`[data-cat="${c}"]`);
      if (el && s) el.scrollTop = s.offsetTop - el.offsetTop;
    });
  };
  const shown = hover ?? groups[0]?.[1][0] ?? null;
  return (
    <div className="emo" onPointerLeave={() => setHover(null)}>
      <div className="emo-top">
        <label className="emo-search">
          <Icon name="search" size="s" />
          <input
            type="search"
            placeholder={t('hub.emoji.search')}
            aria-label={t('hub.emoji.search')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </label>
      </div>
      <div className="emo-tabs" role="tablist" aria-label={t('chat.emoji.categories')}>
        {cats.map((c) => (
          <button
            key={c}
            type="button"
            role="tab"
            className="emo-tab"
            aria-selected={!q && active === c}
            aria-label={t(LABEL[c])}
            data-tip={t(LABEL[c])}
            onClick={() => go(c)}
          >
            {/* the server's tab shows its first emoji, as Discord shows the server */}
            <Emoji e={c === 'server' ? server[0]!.e : TAB[c]} />
          </button>
        ))}
      </div>
      <div className="emo-body nx-scroll" ref={body}>
        {empty ? <p className="emo-none">{t('hub.emoji.none')}</p> : null}
        {groups.map(([k, list]) =>
          list.length ? (
            <section key={k} data-cat={k} aria-label={k === 'results' ? undefined : t(LABEL[k])}>
              {k !== 'results' ? <h3 className="emo-h">{t(LABEL[k])}</h3> : null}
              <div className="emo-grid">
                {list.map((it) => (
                  <button
                    key={it.e}
                    type="button"
                    className="emo-b"
                    aria-label={it.n || it.e}
                    onPointerEnter={() => setHover(it)}
                    onFocus={() => setHover(it)}
                    onClick={(ev) => pick(it, ev.shiftKey)}
                  >
                    <Emoji e={it.e} />
                  </button>
                ))}
              </div>
            </section>
          ) : null,
        )}
      </div>
      <div className="emo-foot" aria-hidden="true">
        {shown ? (
          <>
            <Emoji e={shown.e} cls="emo-foot-e" />
            <span className="emo-foot-n" dir="ltr">
              :{emojiName(shown.e) || shown.e}:
            </span>
          </>
        ) : null}
      </div>
    </div>
  );
}

/** The picker as a sheet on a phone (§12.7 A5): what it gives the pick to. */
export const useEmojiSheet = create<{ open: { onPick: (e: string) => void } | null }>(() => ({
  open: null,
}));

/**
 * The emoji picker on a phone: a bottom sheet over the scrim (grip, Back,
 * a tap outside), the grid fitted to the width. Mounted once (App).
 */
export function EmojiSheetHost() {
  const { t } = useT();
  const open = useEmojiSheet((s) => s.open);
  if (!open) return null;
  const close = () => useEmojiSheet.setState({ open: null });
  return (
    <Sheet label={t('hub.cmp.emoji')} onClose={close} cls="emo-sheet">
      <EmojiPicker onPick={open.onPick} close={close} />
    </Sheet>
  );
}

export function openEmoji(
  anchor: Anchor,
  onPick: (e: string) => void,
  placement: Placement = 'top-end',
): void {
  if (readLayout().layout === 'phone') {
    useEmojiSheet.setState({ open: { onPick } });
    return;
  }
  openPopover(anchor, (close) => <EmojiPicker onPick={onPick} close={close} />, {
    placement,
    cls: 'emo-pop',
    focusSel: 'input',
  });
}
